/** biome-ignore-all lint/a11y/useSemanticElements: <generated by shadcn/ui> */
"use client";

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  holdMediaForMotion,
  markMediaMotionQuiet,
} from "@/lib/media/media-motion";
import {
  refreshVideoProximity,
  warmVideosIn,
} from "@/lib/media/video-proximity";
import { consumeRailScroll, registerScrollRail } from "@/lib/navigation-scroll";
import { cn } from "@/lib/utils";

type CarouselApi = UseEmblaCarouselType[1];

/**
 * How far from its resting place the track may still be when a tap lands and
 * the tap still counts as a click. Embla's own drag threshold — below it the
 * snap it is finishing is the one the finger is on.
 */
const MOVING_TAP_THRESHOLD_PX = 10;
/**
 * How close to its snap the track must be to count as at rest for media
 * scheduling. Sub-pixel: the card has visibly stopped, embla has not.
 */
const REST_PX = 1;
/**
 * After a glide has rested, how far the track must move again before it
 * counts as new travel. Embla's spring overshoots by a pixel or two at the
 * end, which otherwise re-took the hold and ran a second page-wide pass.
 */
const REARM_PX = 8;
/**
 * How close to its snap a glide must be before deferred warm-ups may run.
 * Embla eases out, so here the track moves a pixel or two a frame and a late
 * frame cannot be seen — and a card warmed now is ready for a swipe that
 * interrupts the rest of the glide.
 */
const QUIET_PX = 24;
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
  /**
   * Remembers which slide was on screen across a navigation away and back.
   *
   * Embla drives its track with a transform, not `scrollLeft`, so the position
   * that has to survive is the slide index — the same thing every other snap
   * carousel here reports. Give each carousel on a page a distinct key; keys
   * only have to be unique within a route, because a staged position is handed
   * back on the href it was captured on.
   */
  restoreKey?: string;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  selectedIndex: number;
} & CarouselProps;

/**
 * Split into two contexts on purpose.
 *
 * `selectedIndex` / `canScrollPrev` / `canScrollNext` change on every snap. When
 * they lived on the same context that `CarouselContent` and `CarouselItem` read,
 * every slide — and every component inside every slide — re-rendered each time
 * the user swiped. On a carousel of media cards that meant re-running the whole
 * card tree mid-gesture.
 *
 * Structural values (the embla ref, api, orientation, scroll actions) go in
 * `CarouselStructureContext` and are referentially stable; per-snap values go in
 * `CarouselSelectionContext` and are read only by the dots and the arrow
 * buttons. `useCarousel()` still exposes the union for outside callers.
 */
type CarouselStructureValue = Omit<
  CarouselContextProps,
  "canScrollPrev" | "canScrollNext" | "selectedIndex"
>;
type CarouselSelectionValue = Pick<
  CarouselContextProps,
  "canScrollPrev" | "canScrollNext" | "selectedIndex"
>;

const CarouselStructureContext =
  React.createContext<CarouselStructureValue | null>(null);
const CarouselSelectionContext =
  React.createContext<CarouselSelectionValue | null>(null);

function useCarouselStructure() {
  const context = React.useContext(CarouselStructureContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function useCarouselSelection() {
  const context = React.useContext(CarouselSelectionContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function useCarousel(): CarouselContextProps {
  return { ...useCarouselStructure(), ...useCarouselSelection() };
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  restoreKey,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(
    opts?.startIndex || 0,
  );

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const scrollTo = React.useCallback(
    (index: number) => {
      if (index === api?.selectedScrollSnap()) return;
      const autoplay = api?.plugins()?.autoplay;
      (autoplay as unknown as { reset: () => void })?.reset();
      api?.scrollTo(index);
    },
    [api],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      scrollPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      scrollNext();
    }
  };

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api || !restoreKey) return;
    const root = api.rootNode();

    return registerScrollRail({
      key: restoreKey,
      // Next keeps the route you navigated away from mounted in a hidden
      // subtree, where `offsetParent` is null. A stale carousel in there must
      // not overwrite the live page's entry.
      read: () =>
        root.isConnected && root.offsetParent !== null
          ? api.selectedScrollSnap()
          : null,
    });
  }, [api, restoreKey]);

  /**
   * The embla instance that has been torn down, so a stale closure over it can
   * be told apart from the live one.
   *
   * Next does not unmount the route you navigate away from — it hides it in an
   * `<Activity>` boundary, which destroys effects on hide and re-creates them
   * on show. Coming back therefore re-runs `useEmblaCarousel`'s effect, which
   * destroys the carousel and builds a new one, *and* the restore effect below
   * — but the latter still closes over the previous render's `api`: the
   * instance that was just destroyed, whose `scrollTo` returns immediately.
   * The saved position is single-shot by design, so spending it there left
   * nothing for the live instance a render later and every back navigation
   * landed the rail on its first card.
   *
   * Nothing observable separates the two after the fact — a destroyed instance
   * keeps reporting the index it held, so "did the jump land?" answers yes for
   * the one that did nothing. The tear-down itself is the only honest signal,
   * so it is recorded as it happens.
   *
   * The listener is deliberately not removed on cleanup: `destroy()` clears
   * embla's own handlers and the instance is discarded straight after, so
   * there is nothing to leak — and removing it would make this depend on React
   * tearing effects down in declaration order, which is exactly the kind of
   * assumption this is here to stop relying on.
   */
  const destroyedApiRef = React.useRef<CarouselApi>(undefined);

  React.useEffect(() => {
    if (!api) return;
    api.on("destroy", () => {
      destroyedApiRef.current = api;
    });
  }, [api]);

  /**
   * The slide this rail was on when its route was hidden, carried across the
   * tear-down in memory — the same mechanism `useRailScrollRestore` uses for
   * plain overflow rails, whose `scrollLeft` `display: none` discards.
   *
   * It covers the case the staged record cannot: an `<Activity>` reveal with
   * nothing in sessionStorage to read back, because the reader arrived by
   * something other than a back press or because the staged record aged out.
   * The new embla instance always starts at slide 0, so without this the rail
   * silently rewinds whenever the record is missing.
   */
  const carriedIndexRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    // The stale instance bails BEFORE the read, so the position stays intact
    // for the live one a render later.
    if (!api || !restoreKey || destroyedApiRef.current === api) return;
    const root = api.rootNode();
    if (!root.isConnected || root.offsetParent === null) return;

    const carried = carriedIndexRef.current;
    carriedIndexRef.current = null;
    // What this rail was showing beats what was staged for it: it is the more
    // recent of the two, and consuming the record is what keeps a stale copy
    // in a hidden subtree from claiming the live page's position.
    const saved = carried ?? consumeRailScroll(restoreKey);

    // `true` jumps rather than animating: the reader is arriving, not swiping.
    if (saved !== null && saved > 0) api.scrollTo(saved, true);

    return () => {
      // Runs while this instance still knows where it was — embla keeps its
      // index through `destroy()`, so the order React tears the two effects
      // down in does not matter.
      carriedIndexRef.current = api.selectedScrollSnap() || null;
    };
  }, [api, restoreKey]);

  /**
   * A tap that lands while the track is still gliding stops it — it must not
   * also open the card under the finger.
   *
   * Embla freezes the track on the press, then on release snaps to whichever
   * slide is *nearest*, which is often not the one the finger landed on. Its
   * own click guard only fires after a drag, so the tap went through as a
   * click on the card that happened to be sliding past: the reader saw the
   * carousel settle on one hiive and was taken to its neighbour (on a device
   * the click lands a frame or two after release, so the card under the
   * finger has moved on as well). Native scroll views treat a tap during
   * momentum as "stop" and nothing else; this gives the carousel the same
   * rule.
   *
   * `pointerdown` in the capture phase runs before embla's own `touchstart`
   * / `mousedown` handler, which is what zeroes the distance being read, and
   * fires once per press — iOS follows a tap with emulated mouse events that
   * would otherwise re-measure a track embla has already started snapping.
   */
  React.useEffect(() => {
    if (!api) return;
    const root = api.rootNode();
    let pressedWhileMoving = false;

    const handlePointerDown = () => {
      const engine = api.internalEngine();
      pressedWhileMoving =
        Math.abs(engine.target.get() - engine.location.get()) >
        MOVING_TAP_THRESHOLD_PX;
    };

    const handleClick = (event: MouseEvent) => {
      // `detail === 0` is a keyboard activation, which no press preceded.
      if (!pressedWhileMoving || event.detail === 0) return;
      pressedWhileMoving = false;
      event.preventDefault();
      event.stopPropagation();
    };

    root.addEventListener("pointerdown", handlePointerDown, {
      capture: true,
      passive: true,
    });
    root.addEventListener("click", handleClick, true);

    return () => {
      root.removeEventListener("pointerdown", handlePointerDown, true);
      root.removeEventListener("click", handleClick, true);
    };
  }, [api]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);
    api.on("reInit", refreshVideoProximity);
    refreshVideoProximity();

    return () => {
      api.off("reInit", refreshVideoProximity);
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);

  /**
   * Holds page-wide media decisions while the track moves; see
   * `media-motion.ts`.
   *
   * - `drag` (press → lift): the playing slot moves only to a card dragged
   *   decisively into view. Lifting re-picks at once, so the card flung in
   *   starts during its glide.
   * - `travel` (press or first `scroll` → rest): the page-wide proximity pass
   *   waits. Each time a held drag crosses onto another card, and on lift,
   *   the cards around the one it is heading for are promoted by slide index
   *   (no layout read), so they have sources before they land. Their decode
   *   warm-ups wait for a quiet track: the last `QUIET_PX` of the glide, or
   *   a finger catching it.
   *
   * "Rest" is the track within `REST_PX` of its target, not embla's `settle`:
   * that needs 0.001px and arrives 0.9–1.6s after release, long after the card
   * has visibly stopped. Embla only emits `settle` after an animation it
   * started, so a press that never moved the track rests on `pointerUp`. A
   * programmatic `scrollTo` has no press; its first `scroll` frame takes the
   * hold. The idle timer catches a glide that stops emitting (a reInit).
   */
  React.useEffect(() => {
    if (!api) return;
    let releaseDrag: (() => void) | null = null;
    let releaseTravel: (() => void) | null = null;
    let idle: ReturnType<typeof setTimeout> | undefined;
    let draggedOnto = -1;
    /** This glide has reached rest; only a press or real travel re-arms it. */
    let rested = true;
    /** Deferred warm-ups have been let run for this glide. */
    let quiet = true;

    const holdTravel = () => {
      releaseTravel ??= holdMediaForMotion("travel");
    };
    const endDrag = () => {
      releaseDrag?.();
      releaseDrag = null;
    };
    const endTravel = () => {
      if (releaseTravel) rested = true;
      clearTimeout(idle);
      releaseTravel?.();
      releaseTravel = null;
    };
    const end = () => {
      endDrag();
      endTravel();
    };
    const distanceToRest = () => {
      const engine = api.internalEngine();
      return Math.abs(engine.target.get() - engine.location.get());
    };
    const pressed = () => api.internalEngine().dragHandler.pointerDown();
    /** The slide the track is nearest to right now; no layout read. */
    const nearestSlide = () =>
      api.internalEngine().scrollTarget.byDistance(0, false).index;

    /**
     * The cards around snap `index` (and it), by embla's own registry — no
     * layout read, so it is safe mid-glide. `warmVideosIn` gives them sources.
     */
    const warmAround = (index: number) => {
      const engine = api.internalEngine();
      const snaps = engine.slideRegistry;
      const count = snaps.length;
      const nodes = api.slideNodes();
      const around: Element[] = [];
      for (const offset of [0, 1, -1]) {
        let snap = index + offset;
        if (engine.options.loop) snap = (snap + count) % count;
        for (const slide of snaps[snap] ?? []) {
          const node = nodes[slide];
          if (node) around.push(node);
        }
      }
      warmVideosIn(around);
    };

    const onPointerDown = () => {
      // Caught mid-glide: embla freezes the track under the finger, a moment
      // deferred decode warm-ups can run without a visible hitch.
      if (releaseTravel && !quiet && distanceToRest() > REST_PX)
        markMediaMotionQuiet();
      rested = false;
      quiet = false;
      releaseDrag ??= holdMediaForMotion("drag");
      holdTravel();
      draggedOnto = nearestSlide();
    };
    const onPointerUp = () => {
      endDrag();
      if (distanceToRest() <= REST_PX) endTravel();
      else warmAround(api.internalEngine().index.get());
    };
    const onScroll = () => {
      // Checked before holding: embla keeps emitting through its sub-pixel
      // tail, and re-taking the hold there would release it — and re-run the
      // page-wide pass — on every one of those frames.
      const distance = distanceToRest();
      if (!pressed() && distance <= REST_PX) {
        endTravel();
        return;
      }
      // The spring's last overshoot is not new travel; a programmatic
      // `scrollTo` is.
      if (rested && !pressed() && distance <= REARM_PX) return;
      rested = false;
      holdTravel();
      if (!quiet && !pressed() && distance <= QUIET_PX) {
        quiet = true;
        markMediaMotionQuiet();
      }
      // A held drag that crosses onto another card warms it and its
      // neighbours once per card crossed, not per frame, so a long drag never
      // reaches a card nobody attached a source to.
      if (pressed()) {
        const slide = nearestSlide();
        if (slide !== draggedOnto) {
          draggedOnto = slide;
          warmAround(slide);
        }
      }
      clearTimeout(idle);
      idle = setTimeout(() => {
        if (!pressed()) endTravel();
      }, 400);
    };

    api.on("pointerDown", onPointerDown);
    api.on("pointerUp", onPointerUp);
    api.on("scroll", onScroll);
    api.on("settle", endTravel);
    api.on("destroy", end);

    return () => {
      api.off("pointerDown", onPointerDown);
      api.off("pointerUp", onPointerUp);
      api.off("scroll", onScroll);
      api.off("settle", endTravel);
      api.off("destroy", end);
      end();
    };
  }, [api]);

  const resolvedOrientation =
    orientation || (opts?.axis === "y" ? "vertical" : "horizontal");

  const structure = React.useMemo<CarouselStructureValue>(
    () => ({
      carouselRef,
      api,
      opts,
      orientation: resolvedOrientation,
      scrollPrev,
      scrollNext,
      scrollTo,
    }),
    [
      api,
      carouselRef,
      opts,
      resolvedOrientation,
      scrollNext,
      scrollPrev,
      scrollTo,
    ],
  );

  const selection = React.useMemo<CarouselSelectionValue>(
    () => ({ canScrollPrev, canScrollNext, selectedIndex }),
    [canScrollNext, canScrollPrev, selectedIndex],
  );

  return (
    <CarouselStructureContext.Provider value={structure}>
      <CarouselSelectionContext.Provider value={selection}>
        <div
          onKeyDownCapture={handleKeyDown}
          className={cn("relative", className)}
          role="region"
          aria-roledescription="carousel"
          data-slot="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselSelectionContext.Provider>
    </CarouselStructureContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation } = useCarouselStructure();

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div
        className={cn(
          "flex",
          // Embla's recommended container CSS: the browser keeps the cross
          // axis and pinch, and never has to guess who owns a diagonal pan.
          orientation === "horizontal"
            ? "-ml-4 touch-pan-y touch-pinch-zoom"
            : "-mt-4 touch-pan-x touch-pinch-zoom flex-col",
          className,
        )}
        {...props}
      />
    </div>
  );
}

/**
 * CarouselDots by @plettj, 2025-04-30
 *
 * Currently only works with horizontal orientation.
 */
function CarouselDots({
  className,
  onClick,
  ...props
}: React.ComponentProps<"div">) {
  const { scrollTo, api } = useCarouselStructure();
  const { selectedIndex } = useCarouselSelection();

  return (
    <div
      role="tablist"
      className={cn(
        "absolute bottom-0 flex w-full items-center justify-center gap-2",
        className,
      )}
      {...props}
    >
      {api?.scrollSnapList().map((_, index) => (
        <button
          // biome-ignore lint/suspicious/noArrayIndexKey: carousel dots have no unique id
          key={`carousel-dot-${index + 1}`}
          type="button"
          role="tab"
          data-slot="carousel-dot"
          aria-selected={index === selectedIndex}
          aria-controls="carousel-item"
          aria-label={`Slide ${index + 1}`}
          className={cn(
            "size-2.5 cursor-pointer rounded-full border border-white/50 transition-all",
            index === selectedIndex ? "scale-110 bg-white" : "bg-transparent",
          )}
          onClick={(e) => {
            scrollTo(index);
            onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
          }}
        />
      ))}
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarouselStructure();

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className,
      )}
      {...props}
    />
  );
}

function CarouselPrevious({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev } = useCarouselStructure();
  const { canScrollPrev } = useCarouselSelection();

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal"
          ? "top-1/2 -left-12 -translate-y-1/2"
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowLeft />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

function CarouselNext({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext } = useCarouselStructure();
  const { canScrollNext } = useCarouselSelection();

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal"
          ? "top-1/2 -right-12 -translate-y-1/2"
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowRight />
      <span className="sr-only">Next slide</span>
    </Button>
  );
}

export {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useCarousel,
};
